/**
 * Actor references. An actor is a person, a group, or an automation. References
 * are stored and compared as encoded strings "<kind>:<id>" so they are
 * queryable and contain only stable ids, never PII. This matches the
 * `automation:X` notation in the design's event log.
 */
export const REF_KINDS = ['person', 'group', 'automation'] as const;
export type RefKind = (typeof REF_KINDS)[number];

export interface Ref {
  kind: RefKind;
  id: string;
}

export function formatRef(ref: Ref): string {
  return `${ref.kind}:${ref.id}`;
}

export function parseRef(encoded: string): Ref {
  const idx = encoded.indexOf(':');
  if (idx === -1) throw new Error(`Invalid ref (missing "<kind>:"): ${encoded}`);
  const kind = encoded.slice(0, idx);
  const id = encoded.slice(idx + 1);
  if (!(REF_KINDS as readonly string[]).includes(kind)) {
    throw new Error(`Invalid ref kind "${kind}" in: ${encoded}`);
  }
  if (id.length === 0) throw new Error(`Invalid ref (empty id): ${encoded}`);
  return { kind: kind as RefKind, id };
}

/** Whether two encoded refs denote the same actor. */
export function refsEqual(a: string, b: string): boolean {
  return a === b;
}

export function isGroupRef(encoded: string): boolean {
  return encoded.startsWith('group:');
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether an id is a uuid. A group's id is a `groups.id` and an item's an
 *  `work_items.id`, both uuid columns - so a ref whose id fails this can name no
 *  row, and asking the DB anyway raises a type error instead of returning empty. */
export function isUuid(id: string): boolean {
  return UUID.test(id);
}

// Constructors for ergonomic call sites and tests.
export const person = (id: string): Ref => ({ kind: 'person', id });
export const group = (id: string): Ref => ({ kind: 'group', id });
export const automation = (id: string): Ref => ({ kind: 'automation', id });

/** The encoded ref for a person id ("person:<id>"). The log only ever holds this
 *  string; the PII (name, email) lives in `persons`, resolved separately. This is
 *  the bridge from a better-auth user id to the work-graph actor it acts as. */
export const personRef = (personId: string): string => formatRef(person(personId));
